import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaEye, FaTrashAlt, FaUpload } from 'react-icons/fa';
import Swal from 'sweetalert2';
import inversionesExteriorService from '../../services/inversionesExteriorService';
import useYearSelector from '../../hooks/useYearSelector';
import YearSelector from '../common/YearSelector';
import PropuestaCarga from './PropuestaCarga';
import Boton from './Boton';
import { ESTADOS_CARGA, formatearFechaHora } from './formatos';
import { showError, showSuccess, showLoading, closeLoading } from '../../utils/alerts';
import '../../styles/components/CargasFuente.css';

const observaciones = (carga) => {
    const partes = [];
    if (carga.filas_con_error > 0) partes.push(`${carga.filas_con_error} con error`);
    if (carga.filas_con_aviso > 0) partes.push(`${carga.filas_con_aviso} con aviso`);
    return partes.join(' · ') || '—';
};

/**
 * Página de una fuente de archivos del módulo Inversiones exterior: año de los
 * movimientos, subida del archivo, cargas del año y la propuesta de la carga abierta.
 * La usan las tarjetas de las plataformas (Racional, …); cada una pone su título,
 * su ayuda y su fuente, y el servidor usa el lector de esa fuente.
 *
 * @param {Object} props
 * @param {string} props.token - Token de autenticación
 * @param {number} props.idCompany - Empresa
 * @param {string} props.fuente - Fuente de los archivos (racional, …)
 * @param {string} props.titulo - Nombre de la fuente
 * @param {React.ReactNode} props.icono - Ícono del título
 * @param {React.ReactNode} props.ayuda - Qué archivo subir y qué hace TAXI con él
 * @param {string} props.extensiones - Extensiones que acepta el selector de archivo
 * @param {Function} [props.subir] - Sube el archivo de la fuente; por defecto, la subida común de CSV
 * @param {string} [props.mensajeLectura] - Texto mientras el servidor lee el archivo
 * @param {Function} [props.extraPropuesta] - Recibe la carga abierta y muestra lo propio de la fuente
 */
const CargasFuente = ({
    token, idCompany, fuente, titulo, icono, ayuda, extensiones,
    subir = inversionesExteriorService.subirCarga.bind(inversionesExteriorService),
    mensajeLectura = 'Leyendo el archivo',
    extraPropuesta
}) => {
    const { selectedYear, setSelectedYear, yearOptions } = useYearSelector(null, 2, 0);
    const [archivo, setArchivo] = useState(null);
    const [subiendo, setSubiendo] = useState(false);
    const [cargas, setCargas] = useState(null);
    const [abierta, setAbierta] = useState(null);
    const [paises, setPaises] = useState(null);
    const inputArchivo = useRef(null);

    // La lista de países (columna País) se usa al editar filas
    useEffect(() => {
        const buscarPaises = async () => {
            try {
                setPaises(await inversionesExteriorService.getPaises(token));
            } catch (error) {
                showError('No se pudieron obtener los países', error.message);
            }
        };
        buscarPaises();
    }, [token]);

    const buscarCargas = useCallback(async () => {
        try {
            setCargas(await inversionesExteriorService.getCargas({ idCompany, anioComercial: selectedYear, fuente }, token));
        } catch (error) {
            showError('No se pudieron obtener las cargas', error.message);
        }
    }, [idCompany, selectedYear, fuente, token]);

    useEffect(() => {
        setCargas(null);
        setAbierta(null);
        buscarCargas();
    }, [buscarCargas]);

    const abrirCarga = async (idCarga) => {
        try {
            setAbierta(await inversionesExteriorService.getCarga(idCarga, token));
        } catch (error) {
            showError('No se pudo abrir la carga', error.message);
        }
    };

    // Recarga la propuesta abierta y la lista: avisos, totales, conteos y estado cambian
    const recargar = async () => {
        await abrirCarga(abierta.id);
        buscarCargas();
    };

    // Hace un cambio en la carga abierta y recarga; si falla, muestra el error y devuelve false
    const cambiar = async (accion, mensajeError) => {
        try {
            await accion();
        } catch (error) {
            showError(mensajeError, error.message);
            return false;
        }
        await recargar();
        return true;
    };

    const confirmar = async (opciones) =>
        (await Swal.fire({ showCancelButton: true, cancelButtonText: 'Cancelar', ...opciones })).isConfirmed;

    const acciones = {
        guardarFila: (idFila, valores) => cambiar(
            () => inversionesExteriorService.editarFila(abierta.id, idFila, valores, token),
            'No se guardó la fila'
        ),
        agregarFila: (valores) => cambiar(
            () => inversionesExteriorService.agregarFila(abierta.id, valores, token),
            'No se agregó la fila'
        ),
        cambiarInclusion: (fila) => cambiar(
            () => inversionesExteriorService.cambiarInclusionFila(abierta.id, fila.id, !fila.incluida, token),
            'No se cambió la fila'
        ),
        validar: async () => {
            const incluidas = abierta.filas.filter((fila) => fila.incluida);
            const conAviso = incluidas.filter((fila) => fila.avisos.length > 0).length;
            const confirmado = await confirmar({
                title: '¿Validar esta carga?',
                text: `${incluidas.length} filas incluidas pasan a la DJ 1929 del AT ${abierta.anio_comercial + 1}.`
                    + `${conAviso > 0 ? ` ${conAviso} tienen avisos.` : ''} Después no se puede cambiar sin reabrirla.`,
                icon: 'question',
                confirmButtonText: 'Sí, validar'
            });
            if (!confirmado) return;
            try {
                await inversionesExteriorService.validarCarga(abierta.id, token);
                showSuccess('Carga validada');
            } catch (error) {
                showError('No se validó la carga', error.message);
            }
            // También si falla: el servidor vuelve a calcular y guarda los avisos al validar
            await recargar();
        },
        reabrir: async () => {
            const confirmado = await confirmar({
                title: '¿Reabrir esta carga?',
                text: 'Vuelve a «por revisar» y deja de contar para la DJ 1929 hasta que la valides de nuevo.',
                icon: 'warning',
                confirmButtonText: 'Sí, reabrir'
            });
            if (confirmado) {
                await cambiar(() => inversionesExteriorService.reabrirCarga(abierta.id, token), 'No se reabrió la carga');
            }
        }
    };

    const handleSubir = async (e) => {
        e.preventDefault();
        if (!archivo) return;

        setSubiendo(true);
        showLoading(mensajeLectura, archivo.name);
        try {
            const { data } = await subir({ idCompany, anioComercial: selectedYear, fuente, archivo }, token);
            closeLoading();
            Swal.fire({
                icon: 'success',
                title: 'Carga lista para revisar',
                text: `${data.filas} filas leídas`
                    + `${data.filas_con_error > 0 ? `, ${data.filas_con_error} con errores` : ''}`
                    + `${data.filas_con_aviso > 0 ? `, ${data.filas_con_aviso} con avisos` : ''}.`
            });
            setArchivo(null);
            inputArchivo.current.value = '';
            await buscarCargas();
            abrirCarga(data.id);
        } catch (error) {
            closeLoading();
            showError('No se cargó el archivo', error.message);
        } finally {
            setSubiendo(false);
        }
    };

    const handleDescartar = async (carga) => {
        const { isConfirmed } = await Swal.fire({
            title: '¿Descartar esta carga?',
            text: `«${carga.nombre_archivo}» deja de contar para la DJ 1929. Podrás volver a subir el archivo.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, descartar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33'
        });
        if (!isConfirmed) return;

        try {
            await inversionesExteriorService.descartarCarga(carga.id, token);
            if (abierta?.id === carga.id) setAbierta(null);
            buscarCargas();
        } catch (error) {
            showError('No se descartó la carga', error.message);
        }
    };

    return (
        <div className="invext-cf-container">
            <div className="invext-cf-header">
                <h1>{icono} {titulo}</h1>
                <p>Archivos para la Declaración Jurada 1929</p>
            </div>

            <div className="invext-cf-seccion">
                <div className="invext-cf-anio">
                    <YearSelector
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                        yearOptions={yearOptions}
                        label="Año de los movimientos"
                    />
                    <p className="invext-cf-ayuda">
                        Los movimientos de {selectedYear} se declaran en el AT {selectedYear + 1}.
                    </p>
                </div>
                <p className="invext-cf-ayuda">{ayuda}</p>
                <form className="invext-cf-form" onSubmit={handleSubir}>
                    <input
                        ref={inputArchivo}
                        type="file"
                        accept={extensiones}
                        onChange={(e) => setArchivo(e.target.files[0] || null)}
                        disabled={subiendo}
                    />
                    <Boton type="submit" variante="primario" icono={<FaUpload />} disabled={subiendo || !archivo}>
                        {subiendo ? 'Cargando...' : 'Cargar archivo'}
                    </Boton>
                </form>
            </div>

            <div className="invext-cf-seccion">
                <h2>Cargas de {selectedYear}</h2>
                {cargas === null ? null : cargas.length === 0 ? (
                    <p className="invext-cf-vacio">Todavía no hay cargas de {titulo} para {selectedYear}.</p>
                ) : (
                    <div className="invext-cf-tabla-wrap">
                        <table className="invext-cf-tabla">
                            <thead>
                                <tr>
                                    <th>Archivo</th>
                                    <th>Subida</th>
                                    <th>Estado</th>
                                    <th className="num">Filas</th>
                                    <th>Observaciones</th>
                                    <th aria-label="Acciones" />
                                </tr>
                            </thead>
                            <tbody>
                                {cargas.map((carga) => (
                                    <tr key={carga.id} className={abierta?.id === carga.id ? 'abierta' : ''}>
                                        <td>
                                            {carga.url_archivo ? (
                                                <a href={carga.url_archivo} target="_blank" rel="noopener noreferrer">{carga.nombre_archivo}</a>
                                            ) : carga.nombre_archivo}
                                            {carga.institucion && <span className="invext-cf-institucion">{carga.institucion}</span>}
                                        </td>
                                        <td>{formatearFechaHora(carga.created_at)}</td>
                                        <td><span className={`invext-cf-estado ${carga.estado}`}>{ESTADOS_CARGA[carga.estado]}</span></td>
                                        <td className="num">{carga.filas}</td>
                                        <td>{observaciones(carga)}</td>
                                        <td>
                                            <div className="invext-botones en-fila derecha">
                                                <Boton
                                                    chico
                                                    icono={<FaEye />}
                                                    aria-pressed={abierta?.id === carga.id}
                                                    onClick={() => abrirCarga(carga.id)}
                                                >
                                                    Ver
                                                </Boton>
                                                {carga.estado === 'por_revisar' && (
                                                    <Boton chico variante="peligro" icono={<FaTrashAlt />} onClick={() => handleDescartar(carga)}>
                                                        Descartar
                                                    </Boton>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {abierta && (
                <PropuestaCarga
                    carga={abierta}
                    paises={paises}
                    acciones={acciones}
                    onCerrar={() => setAbierta(null)}
                />
            )}
            {abierta && extraPropuesta && extraPropuesta(abierta)}
        </div>
    );
};

export default CargasFuente;
